import React, { ReactElement, useState } from "react"
import Select, { SingleValue } from "react-select"
import { FormLabel, TextArea, TextInput } from "@wfp/ui"
import { IChoiceForm, IGroupForm, IQuestionForm } from "interfaces/xlsForm"
import { OptionGroup } from "interfaces/mappingInterfaces"
import {
  useGetAllTypesQuery,
  useGetCategoriesFieldQuery,
  useGetFormListQuery,
  useGetGroupListQuery,
} from "services/household/xlsFormSchemas"
import { filterOption } from "utils/helpers"
import CustomOption from "components/Shared/Common/CustomOption"
import GroupForm from "components/Household/Form/GroupForm"
import ChoiceListForm from "components/Household/Form/ChoiceListForm"

type Props = {
  xlsFormId: number
  form: IQuestionForm
  setForm: React.Dispatch<React.SetStateAction<IQuestionForm>>
  groupForm: IGroupForm
  setGroupForm: React.Dispatch<React.SetStateAction<IGroupForm>>
  choiceForm: IChoiceForm
  setChoiceForm: React.Dispatch<React.SetStateAction<IChoiceForm>>
  categoryType: number
  setCategoryType: React.Dispatch<React.SetStateAction<number>>
}

const initialGroupForm: IGroupForm = {
  name: "",
  label: "",
}

const initialChoiceForm: IChoiceForm = {
  name: "",
}

const QuestionForm = ({
  xlsFormId,
  form,
  setForm,
  groupForm,
  setGroupForm,
  choiceForm,
  setChoiceForm,
  categoryType,
  setCategoryType,
}: Props): ReactElement => {
  const { data: categories, isLoading: loadingCategories } = useGetCategoriesFieldQuery()
  const { data: typeList, isLoading: loadingTypes } = useGetAllTypesQuery(xlsFormId)
  const { data: choiceList, isLoading: loadingChoiceList } = useGetFormListQuery(xlsFormId)
  const { data: groupList, isLoading: loadingGroups } = useGetGroupListQuery(xlsFormId)

  const [isNewGroup, setIsNewGroup] = useState<boolean>(false)
  const [isNewChoiceList, setIsNewChoiceList] = useState<boolean>(false)

  const customStyles = {
    option: (styles, { data, isFocused }) => ({
      ...styles,
      backgroundColor: isFocused ? "#EBF3FB" : "#fff",
      color: !data.count ? "var(--support-01)" : "#000",
    }),
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <FormLabel htmlFor="type" className="required">
          Type
        </FormLabel>
        <Select
          inputId="type"
          placeholder="Select type"
          isLoading={loadingTypes || loadingCategories}
          options={typeList || []}
          getOptionLabel={option => option.name}
          getOptionValue={option => option.id?.toString()}
          value={typeList?.find(item => item.id === form.typeId)}
          onChange={item => {
            setForm(prev => ({ ...prev, typeId: item?.id ?? 0 }))
            setCategoryType(item?.categoryId ?? 0)
          }}
          isSearchable
          isClearable
        />
      </div>
      <TextInput
        labelText="Name"
        name="name"
        formItemClassName="text-required"
        className="custom-text-field"
        onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
      />
      <TextArea
        labelText="Label"
        name="label"
        // @ts-ignore
        formItemClassName="text-required"
        className="custom-text-field"
        style={{ resize: "none" }}
        rows={3}
        onChange={e => setForm(prev => ({ ...prev, label: e.target.value }))}
      />

      {categoryType === categories?.List && (
        <div>
          <div className="d-flex justify-between">
            <FormLabel htmlFor="choice-list" className="required">
              Choice list
            </FormLabel>
            <div
              className="d-flex wfp--label primary-color cursor-pointer text-[13px]"
              onClick={() => {
                setForm(prev => ({ ...prev, choiceListId: 0 }))
                setIsNewChoiceList(prev => !prev)
                setChoiceForm(initialChoiceForm)
              }}
            >
              {isNewChoiceList ? "Back to select" : "Add new choice list"}
            </div>
          </div>

          {isNewChoiceList ? (
            <div className="mt-0.5 bg-gray-200 rounded p-4 pb-5">
              <ChoiceListForm form={choiceForm} setForm={setChoiceForm} />
            </div>
          ) : (
            <Select
              inputId="choice-list"
              isLoading={loadingChoiceList}
              placeholder="Select choice list"
              styles={customStyles}
              options={choiceList || []}
              getOptionLabel={option => option.listName}
              getOptionValue={option => option.listId?.toString()}
              value={choiceList?.flatMap(item => item.options)?.find(option => option.listId === form.choiceListId)}
              onChange={item => setForm(prev => ({ ...prev, choiceListId: item?.listId ?? 0 }))}
              isSearchable
              isClearable
              formatOptionLabel={option => {
                return (
                  <div className="flex items-center justify-between">
                    <div>{option.listName}</div>
                    <div className="text-xs">{option.count ?? 0}</div>
                  </div>
                )
              }}
            />
          )}
        </div>
      )}

      <div>
        <div className="d-flex justify-between">
          <FormLabel htmlFor="group">Group</FormLabel>
          <div
            className="d-flex wfp--label primary-color cursor-pointer text-[13px]"
            onClick={() => {
              setForm(prev => ({ ...prev, groupId: 0 }))
              setIsNewGroup(prev => !prev)
              setGroupForm(initialGroupForm)
            }}
          >
            {isNewGroup ? "Back to select" : "Add new group"}
          </div>
        </div>

        {isNewGroup ? (
          <div className="mt-0.5 bg-gray-200 rounded p-4 pb-5">
            <GroupForm xlsFormId={xlsFormId} form={groupForm} setForm={setGroupForm} />
          </div>
        ) : (
          <Select
            inputId="group"
            placeholder="Select group"
            className="select-wrapper"
            classNamePrefix="select"
            isLoading={loadingGroups}
            options={groupList || []}
            getOptionLabel={option => option.label}
            getOptionValue={option => option.value}
            isSearchable
            components={{ Option: CustomOption }}
            value={groupList?.find(item => Number(item.value) === form.groupId)}
            onChange={item => {
              setForm(prev => ({ ...prev, groupId: Number((item as SingleValue<OptionGroup>)?.value) }))
            }}
            isClearable
            filterOption={filterOption}
          />
        )}
      </div>
    </div>
  )
}

export default QuestionForm
